// Unified Review Workflow Integration — user-facing error genericization.
//
// `toUserFacingError` maps ANY thrown value into a small, safe
// `UserFacingError` shape the UI can render. It is the single classification
// point for this feature's new entry points, hooks, and controls: every failed
// read or state-changing action (reviews-due load, QBR generation, share) funnels
// its caught error through here, so the surface area for leaking internal detail
// is exactly one pure function.
//
// Pure, side-effect-free decision logic (no network, no DOM, no logging). It
// reuses the `ApiError` contract from `@/lib/errorHandling` (the only error type
// thrown by `apiRequest` / `getQueryFn` via `parseErrorResponse`) and mirrors the
// frozen unified-review-ui error pattern.
//
// References: design.md "Pure selectors (client/src/lib/reviewWorkflow/) →
// errors.ts"; requirements 9.3, 10.7.
//
// Guarantees:
//   - Never throws — handles ApiError, native Error (with stack), plain objects,
//     raw strings, and null/undefined.
//   - Always returns a non-empty `message`.
//   - The returned `message` is a fixed, canonical string per `kind`; it NEVER
//     embeds the raw error text, so stack frames, raw exception/SQL text, and
//     server file paths can never leak through it (Req 10.7).
//   - The authorization (403) message is distinct from the validation (400)
//     message (Req 9.3).

import { ApiError } from "@/lib/errorHandling";

/** The classification of a failed request, used to pick a safe message. */
export type UserFacingErrorKind =
  | "validation"
  | "authorization"
  | "notfound"
  | "generic";

/**
 * A genericized, render-safe view of an error. `message` is always a non-empty
 * canonical string chosen by `kind`.
 */
export interface UserFacingError {
  message: string;
  kind: UserFacingErrorKind;
}

// Canonical, internal-detail-free messages. Keeping these fixed (rather than
// echoing server/exception text) is what strips internal detail (Req 10.7): the
// message can never contain a stack frame, SQL fragment, or file path. The
// authorization message is deliberately distinct from the validation message
// (Req 9.3).
const MESSAGES: Record<UserFacingErrorKind, string> = {
  validation:
    "We couldn't complete that because some of the information provided was invalid. Please review your input and try again.",
  authorization: "You don't have permission to perform this action.",
  notfound:
    "We couldn't find the requested item. It may have been removed or you may not have access to it.",
  generic: "Something went wrong. Please try again.",
};

/**
 * Best-effort extraction of an HTTP status code from an arbitrary error value.
 * `ApiError` carries `statusCode`; defensively we also read `status` /
 * `statusCode` off plain objects. Anything else yields `undefined` (→ generic).
 */
function extractStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.statusCode;
  }
  if (error !== null && typeof error === "object") {
    const candidate = error as Record<string, unknown>;
    const raw = candidate.statusCode ?? candidate.status;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
  }
  return undefined;
}

/** Map an HTTP status to a `UserFacingErrorKind` per the design's classification. */
function classifyStatus(status: number | undefined): UserFacingErrorKind {
  switch (status) {
    case 403:
      return "authorization";
    case 400:
      return "validation";
    case 404:
      return "notfound";
    default:
      // Everything else — including 5xx, other 4xx, and unknown — is generic.
      return "generic";
  }
}

/**
 * Convert any thrown value into a safe, render-ready `UserFacingError`.
 *
 * Classification is purely status-driven (403 → authorization, 400 → validation,
 * 404 → notfound, everything else → generic). The message is always a fixed
 * canonical string for the resolved kind, so no internal detail can ever leak
 * through it (Req 10.7), and the authorization message is distinct from the
 * validation message (Req 9.3).
 *
 * Never throws; always returns a non-empty `message`.
 */
export function toUserFacingError(error: unknown): UserFacingError {
  const kind = classifyStatus(extractStatus(error));
  return { message: MESSAGES[kind], kind };
}
